import type { VariantLedgerView, VariantResultView } from "@exploitlens/ui-contracts";

function formatParameters(params: Record<string, unknown>): string {
  return Object.entries(params)
    .map(([key, value]) => `${key}: ${String(value)}`)
    .join(", ");
}

const VALIDITY_PRESENTATION: Record<
  VariantResultView["vulnerableValidity"],
  { symbol: string; text: string }
> = {
  valid: { symbol: "✓", text: "text-ink" },
  invalid: { symbol: "—", text: "text-ink-faint" },
};

const PATCHED_STATUS_PRESENTATION: Record<
  NonNullable<VariantResultView["patchedStatus"]>,
  { symbol: string; text: string }
> = {
  pass: { symbol: "✓", text: "text-preserved" },
  fail: { symbol: "✕", text: "text-loss" },
  inconclusive: { symbol: "?", text: "text-ink-muted" },
};

/**
 * Candidate parameters, vulnerable validity, patched result, and the valid
 * denominator -- keeping the wording bounded to exactly what the ledger
 * recorded (task doc for EL-064/EL-073), never rounded up to "verified
 * across all variants" or similar.
 */
export function VariantLedger({ ledger }: { ledger: VariantLedgerView }) {
  return (
    <section aria-labelledby="variant-ledger-heading">
      <h2 id="variant-ledger-heading" className="font-editorial text-xl text-ink">
        Variant ledger
      </h2>
      <p className="mt-2 text-sm text-ink-muted">
        {ledger.validDenominator} of {ledger.results.length} candidate(s) validated on the
        vulnerable commit (seed {ledger.seed}, budget {ledger.budget}). The neighborhood gate is
        computed from exactly this bounded, seeded set.
      </p>

      <div className="mt-4 overflow-x-auto rounded border border-rule">
        <table className="w-full min-w-[36rem] text-left text-sm">
          <thead>
            <tr className="border-b border-rule bg-canvas-raised text-ink-faint">
              <th scope="col" className="px-3 py-2 font-medium">
                Candidate
              </th>
              <th scope="col" className="px-3 py-2 font-medium">
                Strategy
              </th>
              <th scope="col" className="px-3 py-2 font-medium">
                Parameters
              </th>
              <th scope="col" className="px-3 py-2 font-medium">
                Vulnerable validity
              </th>
              <th scope="col" className="px-3 py-2 font-medium">
                Patched result
              </th>
            </tr>
          </thead>
          <tbody>
            {ledger.results.map((result) => {
              const validity = VALIDITY_PRESENTATION[result.vulnerableValidity];
              const patched = result.patchedStatus
                ? PATCHED_STATUS_PRESENTATION[result.patchedStatus]
                : undefined;
              return (
                <tr key={result.candidate.id} className="border-b border-rule last:border-0">
                  <td className="px-3 py-2 font-evidence text-ink">{result.candidate.id}</td>
                  <td className="px-3 py-2 text-ink-muted">{result.candidate.strategy}</td>
                  <td className="px-3 py-2 font-evidence text-xs text-ink-muted">
                    {formatParameters(result.candidate.parameters)}
                  </td>
                  <td className={`px-3 py-2 ${validity.text}`}>
                    <span aria-hidden="true">{validity.symbol}</span> {result.vulnerableValidity}
                    {result.invalidReason && (
                      <span className="block text-xs text-ink-faint">{result.invalidReason}</span>
                    )}
                  </td>
                  <td className={`px-3 py-2 ${patched?.text ?? "text-ink-faint"}`}>
                    {patched ? (
                      <>
                        <span aria-hidden="true">{patched.symbol}</span> {result.patchedStatus}
                      </>
                    ) : (
                      "not run"
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
